import { z } from 'zod';

/**
 * ISO 3166-1 alpha-2 country codes — the storage format for every `country`
 * field in the app (users, customer contacts, customer addresses). Display
 * names come from `Intl.DisplayNames`, so the list is just the codes; we
 * never persist or compare localized names.
 *
 * All 249 officially assigned codes, ascending.
 */
export const COUNTRY_CODES = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

const CODE_SET: ReadonlySet<string> = new Set(COUNTRY_CODES);

export function isCountryCode(value: string): value is CountryCode {
  return CODE_SET.has(value);
}

// Singleton — Intl.DisplayNames construction is not cheap and the locale is
// fixed (the app is inline-English; see "Frontend out of scope: i18n").
let displayNames: Intl.DisplayNames | undefined;
function regionNames(): Intl.DisplayNames {
  displayNames ??= new Intl.DisplayNames(['en'], { type: 'region' });
  return displayNames;
}

/**
 * English display name for a stored country value. Falls back to the input
 * verbatim for legacy free-text rows that predate ISO codes, so render
 * sites can call this unconditionally.
 */
export function countryName(value: string): string {
  if (!isCountryCode(value)) return value;
  return regionNames().of(value) ?? value;
}

/**
 * Common inputs that neither the code list nor the Intl English names cover.
 * Keys are lowercased. Deliberately short — this is a courtesy for imports
 * and legacy data, not a localization layer.
 */
const NAME_ALIASES: Record<string, CountryCode> = {
  usa: 'US',
  'u.s.': 'US',
  'u.s.a.': 'US',
  'united states of america': 'US',
  uk: 'GB',
  'u.k.': 'GB',
  'great britain': 'GB',
  england: 'GB',
  uae: 'AE',
  'united arab emirates': 'AE',
  'czech republic': 'CZ',
  'ivory coast': 'CI',
  'cape verde': 'CV',
  burma: 'MM',
  'east timor': 'TL',
  swaziland: 'SZ',
  macedonia: 'MK',
  holland: 'NL',
  'the netherlands': 'NL',
  'republic of korea': 'KR',
  'republic of india': 'IN',
};

// Lowercased English name → code, built lazily on first lookup.
let nameToCode: Map<string, CountryCode> | undefined;
function nameIndex(): Map<string, CountryCode> {
  if (!nameToCode) {
    nameToCode = new Map();
    const names = regionNames();
    for (const code of COUNTRY_CODES) {
      const name = names.of(code);
      if (name && name !== code) nameToCode.set(name.toLowerCase(), code);
    }
    for (const [alias, code] of Object.entries(NAME_ALIASES)) {
      nameToCode.set(alias, code);
    }
  }
  return nameToCode;
}

/**
 * Resolve free-form input to an ISO 3166-1 alpha-2 code: an alpha-2 code in
 * any case, an English country name, or a known alias ("USA", "UK"…).
 * Returns undefined when unresolvable — callers decide whether that's a
 * validation error (API input) or a "please reselect" hint (legacy data in
 * a form).
 */
export function toCountryCode(input: string | null | undefined): CountryCode | undefined {
  const trimmed = input?.trim();
  if (!trimmed) return undefined;
  const upper = trimmed.toUpperCase();
  if (isCountryCode(upper)) return upper;
  return nameIndex().get(trimmed.toLowerCase());
}

const INVALID_COUNTRY_MESSAGE = 'Must be an ISO-3166 country code (e.g. US) or a country name';

/**
 * Resolve `value` to an alpha-2 code or record a validation issue on `ctx`.
 * The single home for the resolve-or-error step — every country schema (and
 * the import mappers, which need their own message and null convention)
 * funnels through here so resolution policy changes in exactly one place.
 *
 * Output is widened to `string`: stored values are plain text columns and
 * legacy rows may predate ISO codes, so a CountryCode-typed field at rest
 * would be a lie. Use `isCountryCode` to narrow where it matters.
 */
export function resolveCountryOrIssue(
  value: string,
  ctx: z.RefinementCtx,
  message: string = INVALID_COUNTRY_MESSAGE,
): string {
  const code = toCountryCode(value);
  if (!code) {
    ctx.addIssue({ code: 'custom', message });
    return z.NEVER;
  }
  return code;
}

/**
 * Form-side shape check: empty ('' / null / undefined) is fine, anything
 * else must resolve to a code. For react-hook-form Zod schemas whose
 * country value comes from `CountrySelect` (which only emits codes) — the
 * refine exists for legacy free-text values loaded into the form.
 */
export function isResolvableCountryInput(value: string | null | undefined): boolean {
  return !value || toCountryCode(value) !== undefined;
}

/**
 * Lenient country normalization for bulk ingestion (xlsx/csv imports):
 * '' → null ("not provided"), resolvable code/name/alias → the alpha-2
 * code, anything else → the trimmed raw input. A country cell never fails
 * an import row — real-world files carry unmappable legacy values, and
 * rejecting the row for one cosmetic column turns a migration into a
 * dead import. Unresolved values render via `countryName`'s raw fallback
 * and are flagged for reselection the next time the record is edited.
 * The interactive create/patch API stays strict (`optionalCountryCode`).
 */
export function toStoredCountry(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  return toCountryCode(trimmed) ?? trimmed;
}

/**
 * Required country input: accepts a code / name / alias, stores the alpha-2
 * code. Compose `.optional()` etc. per operation — schemas are
 * per-operation, never shared.
 */
export const countryCodeSchema = z
  .string()
  .trim()
  .max(120)
  .transform((value, ctx) => resolveCountryOrIssue(value, ctx));

/**
 * Optional country input with the app's standard empty-string handling:
 * '' → null ("not provided"), otherwise must resolve to a code. Mirrors the
 * `optionalText` convention used across domain validators.
 */
export const optionalCountryCode = z
  .string()
  .trim()
  .max(120)
  .transform((value, ctx): string | null =>
    value === '' ? null : resolveCountryOrIssue(value, ctx),
  )
  .nullable()
  .optional();

/**
 * Plain-optional variant for object fields with no null in their shape
 * (e.g. address blobs): '' → undefined ("not provided"), otherwise must
 * resolve to a code.
 */
export const plainOptionalCountryCode = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  countryCodeSchema.optional(),
);
